/**
 * Snake Snacks category page (sections/main-collection-explore.liquid).
 *
 * - Level-3 type chips, filters (brand, flavor, texture, allergens, price) and sorting,
 *   all in the URL so links can be shared and refreshed:
 *   ?type=potato&brand=oreo,glico&free=milk&price=under-2&sort=popular
 * - "Featured" keeps each brand's products together, brands A→Z.
 * - "Most popular" uses Shopify's best-selling order (sales volume) across the whole store.
 * - Shopify renders at most 250 products per request; any further pages are fetched and merged
 *   so counts, filters and grouping always cover the whole category.
 *
 * The section already renders products in brand order; this script refines it (title within brand)
 * and applies whatever filters/sort are in the URL.
 */
/** UI words in the shopper's language (window.SnakeI18n is printed by snippets/snake-head.liquid). */
const T = (key, fallback) => (window.SnakeI18n && window.SnakeI18n[key]) || fallback;
(function () {
  'use strict';

  const root = document.querySelector('[data-snk-collection]:not([data-ready])');
  if (!root) return;
  root.setAttribute('data-ready', '');

  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel, el = root) => Array.from(el.querySelectorAll(sel));
  const grid = $('[data-snk-grid]');
  if (!grid) return;

  let cfg = {};
  try {
    cfg = JSON.parse($('[data-snk-config]')?.textContent || '{}');
  } catch (e) {}

  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const isSearch = cfg.mode === 'search';
  const DEFAULT_SORT = isSearch ? 'relevance' : 'featured';
  const SORTS = [DEFAULT_SORT, 'popular', 'price-asc', 'price-desc', 'newest'];
  const LEGACY_SORT = {
    'price-ascending': 'price-asc',
    'price-descending': 'price-desc',
    'best-selling': 'popular',
    'created-descending': 'newest',
  };
  const GROUPS = ['brand', 'flavor', 'texture', 'free', 'price'];
  const PRICE = [
    { key: 'under-2', label: T('price_under2', 'Under $2'), test: (c) => c < 200 },
    { key: '2-3', label: '$2 – $3', test: (c) => c >= 200 && c < 300 },
    { key: '3-5', label: '$3 – $5', test: (c) => c >= 300 && c < 500 },
    { key: '5-plus', label: T('price_5up', '$5 & up'), test: (c) => c >= 500 },
  ];
  const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

  const slug = (s) => {
    const raw = String(s || '').trim();
    const out = raw
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return out || raw.toLowerCase();
  };

  /* ───────── products ───────── */

  /** @type {Map<string, any>} */
  const products = new Map();
  /** label lookups: slug -> {raw label: count} */
  const labels = { brand: new Map(), flavor: new Map(), texture: new Map(), free: new Map(), type: new Map() };

  function remember(group, key, label) {
    if (!key) return;
    const counts = labels[group].get(key) || new Map();
    counts.set(label, (counts.get(label) || 0) + 1);
    labels[group].set(key, counts);
  }

  function labelFor(group, key) {
    if (group === 'price') return (PRICE.find((b) => b.key === key) || {}).label || key;
    const counts = labels[group].get(key);
    if (!counts) return key;
    // the most common spelling wins, e.g. "GLICO" over "Glico"
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  }

  let order = 0;
  function readCard(el) {
    const d = el.dataset;
    const allergenLabels = (d.allergens || '').split(',').map((s) => s.trim()).filter(Boolean);
    const p = {
      el,
      id: d.id,
      title: d.title || '',
      price: Number(d.price) || 0,
      published: Number(d.published) || 0,
      type: d.type || '',
      brand: slug(d.vendor),
      flavor: slug(d.flavor),
      texture: slug(d.texture),
      allergens: allergenLabels.map(slug),
      rel: order++,
    };
    remember('brand', p.brand, (d.vendor || '').trim());
    remember('flavor', p.flavor, (d.flavor || '').trim());
    remember('texture', p.texture, (d.texture || '').trim());
    remember('type', p.type, d.typeLabel || '');
    allergenLabels.forEach((a) => remember('free', slug(a), a.charAt(0).toUpperCase() + a.slice(1)));
    return p;
  }

  function addCards(nodes) {
    for (const el of nodes) {
      const p = readCard(el);
      if (!p.id || products.has(p.id)) {
        el.remove();
        continue;
      }
      products.set(p.id, p);
    }
  }

  addCards($$('[data-snc]', grid));

  /* ───────── state <-> URL ───────── */

  const state = { type: '', sort: DEFAULT_SORT, brand: new Set(), flavor: new Set(), texture: new Set(), free: new Set(), price: new Set() };

  function readUrl() {
    const q = new URLSearchParams(location.search);
    // on the search page "type" is Shopify's own parameter (type=product), not a level-3 filter
    state.type = isSearch ? '' : q.get('type') || '';
    const sort = q.get('sort') || LEGACY_SORT[q.get('sort_by') || ''] || DEFAULT_SORT;
    state.sort = SORTS.includes(sort) ? sort : DEFAULT_SORT;
    GROUPS.forEach((g) => {
      state[g] = new Set((q.get(g) || '').split(',').filter(Boolean));
    });
  }

  function writeUrl() {
    const q = new URLSearchParams(location.search);
    [...(isSearch ? [] : ['type']), 'sort', 'sort_by', 'page', ...GROUPS].forEach((k) => q.delete(k));
    const parts = [];
    q.forEach((v, k) => parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`));
    if (state.type) parts.push(`type=${encodeURIComponent(state.type)}`);
    GROUPS.forEach((g) => {
      if (state[g].size) parts.push(`${g}=${[...state[g]].map(encodeURIComponent).join(',')}`);
    });
    if (state.sort !== DEFAULT_SORT) parts.push(`sort=${state.sort}`);
    history.replaceState(history.state, '', location.pathname + (parts.length ? `?${parts.join('&')}` : '') + location.hash);
  }

  /* ───────── filtering + sorting ───────── */

  function match(p, skip) {
    if (skip !== 'type' && state.type && p.type !== state.type) return false;
    if (skip !== 'brand' && state.brand.size && !state.brand.has(p.brand)) return false;
    if (skip !== 'flavor' && state.flavor.size && !state.flavor.has(p.flavor)) return false;
    if (skip !== 'texture' && state.texture.size && !state.texture.has(p.texture)) return false;
    if (skip !== 'free' && state.free.size && p.allergens.some((a) => state.free.has(a))) return false;
    if (skip !== 'price' && state.price.size && !PRICE.some((b) => state.price.has(b.key) && b.test(p.price))) return false;
    return true;
  }

  const byTitle = (a, b) => collator.compare(a.title, b.title);
  const byBrand = (a, b) => collator.compare(labelFor('brand', a.brand), labelFor('brand', b.brand)) || byTitle(a, b);

  /** @type {Map<string, number> | null} */
  let rank = null;

  function sortList(list) {
    switch (state.sort) {
      case 'price-asc':
        return list.sort((a, b) => a.price - b.price || byBrand(a, b));
      case 'price-desc':
        return list.sort((a, b) => b.price - a.price || byBrand(a, b));
      case 'newest':
        return list.sort((a, b) => b.published - a.published || byBrand(a, b));
      case 'relevance': {
        // keep each brand together, brands in order of their best match; a brand typed by name comes first
        const raw = String(cfg.query || '').trim().toLowerCase();
        const q = slug((cfg.aliases && cfg.aliases[raw]) || raw);
        const first = new Map();
        list.forEach((p) => first.set(p.brand, Math.min(first.has(p.brand) ? first.get(p.brand) : 1e9, p.rel)));
        if (q.length >= 2) {
          first.forEach((v, brand) => {
            const label = slug(labelFor('brand', brand));
            if (label === q || (q.length >= 3 && label.startsWith(q)) || q.split('-').includes(label)) first.set(brand, -1);
          });
        }
        return list.sort((a, b) => first.get(a.brand) - first.get(b.brand) || a.rel - b.rel);
      }
      case 'popular':
        if (rank) {
          const r = rank;
          return list.sort((a, b) => (r.has(a.id) ? r.get(a.id) : 1e9) - (r.has(b.id) ? r.get(b.id) : 1e9) || byBrand(a, b));
        }
        return list.sort(byBrand);
      default:
        return list.sort(byBrand);
    }
  }

  /* ───────── rendering ───────── */

  const els = {
    heading: $('[data-heading]'),
    count: $('[data-count]'),
    empty: $('[data-empty]'),
    loading: $('[data-loading]'),
    sort: /** @type {HTMLSelectElement | null} */ ($('[data-sort]')),
    filterBtn: $('[data-open-filters]'),
    badge: $('[data-filter-badge]'),
    active: $('[data-active-filters]'),
    sheet: /** @type {HTMLDialogElement | null} */ ($('[data-filter-sheet]')),
    groups: $('[data-filter-groups]'),
    showResults: $('[data-show-results]'),
    crumbL2: $('[data-crumb-l2]'),
    crumbL3: $('[data-crumb-l3]'),
    sticky: $('[data-sticky]'),
  };
  const typeChips = $$('[data-type-chip]');
  // desktop sidebar: level-3 links for this same collection filter in place instead of reloading
  const treeTypeLinks = $$('[data-type-link]').filter((a) => {
    try {
      return new URL(/** @type {HTMLAnchorElement} */ (a).href, location.href).pathname === location.pathname;
    } catch (e) {
      return false;
    }
  });
  const expanded = new Set();

  let firstRender = true;
  let replayOnClose = false;

  /** @param {boolean} [animate] replay the cards' entrance (after the shopper changed something) */
  function render(animate = false) {
    const all = [...products.values()];
    const visible = sortList(all.filter((p) => match(p)));
    const frag = document.createDocumentFragment();

    // "Featured" keeps each brand's products next to each other (brands A→Z), in one continuous grid
    visible.forEach((p) => {
      p.el.hidden = false;
      frag.append(p.el);
    });
    const shown = new Set(visible);
    all.forEach((p) => {
      if (!shown.has(p)) {
        p.el.hidden = true;
        frag.append(p.el);
      }
    });
    grid.replaceChildren(frag);
    // bring the cards in again (left to right on category pages); the first time, just after the page header.
    // Changes made inside the filter sheet play when it closes, where the shopper can see them.
    if (animate && els.sheet?.open) replayOnClose = true;
    else if (animate || firstRender) {
      window.SnakeReveal?.replay?.(grid, { base: firstRender && grid.closest('[data-motion="lr"]') ? 60 : 0 });
    }
    firstRender = false;

    const n = visible.length;
    if (els.count) els.count.textContent = n === 1 ? T('one_product', '1 product') : T('n_products', '{n} products').replace('{n}', n);
    if (els.empty) els.empty.hidden = n > 0;
    if (els.sort) els.sort.value = state.sort;
    if (els.showResults) els.showResults.textContent = n ? (n === 1 ? T('show_one', 'Show 1 product') : T('show_n', 'Show {n} products').replace('{n}', n)) : T('no_matches', 'No matches');

    renderLevel3();
    renderActive();
    if (els.sheet && els.sheet.open) renderGroups();

    root.dispatchEvent(
      new CustomEvent('snake:collection-update', {
        bubbles: true,
        detail: { count: n, type: state.type, sort: state.sort, filters: Object.fromEntries(GROUPS.map((g) => [g, [...state[g]]])) },
      })
    );
  }

  function renderLevel3() {
    const typeLabel = state.type ? labelFor('type', state.type) : '';
    if (els.heading) els.heading.textContent = typeLabel || els.heading.dataset.base || '';
    if (els.crumbL3 && els.crumbL2) {
      els.crumbL3.hidden = !typeLabel;
      const span = els.crumbL3.querySelector('span');
      if (span) span.textContent = typeLabel;
      const l2Span = els.crumbL2.querySelector('span');
      const l2Link = els.crumbL2.querySelector('a');
      if (l2Span && l2Link) {
        l2Span.hidden = !!typeLabel;
        l2Link.hidden = !typeLabel;
      }
    }
    treeTypeLinks.forEach((a) => {
      const on = a.getAttribute('data-type-link') === state.type;
      a.closest('.snk-tree__row')?.classList.toggle('is-current', on);
      on ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current');
    });
    if (!typeChips.length) return;
    const base = [...products.values()].filter((p) => match(p, 'type'));
    typeChips.forEach((chip) => {
      const key = chip.getAttribute('data-type-chip') || '';
      chip.setAttribute('aria-pressed', String(key === state.type));
      const count = key ? base.filter((p) => p.type === key).length : base.length;
      let badge = chip.querySelector('.snk-chip__n');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'snk-chip__n';
        chip.append(' ', badge);
      }
      badge.textContent = String(count);
      chip.hidden = key !== '' && count === 0 && key !== state.type;
    });
  }

  function activeCount() {
    return GROUPS.reduce((n, g) => n + state[g].size, 0);
  }

  function renderActive() {
    const n = activeCount();
    if (els.badge) {
      els.badge.hidden = n === 0;
      els.badge.textContent = String(n);
    }
    if (!els.active) return;
    els.active.replaceChildren();
    els.active.hidden = n === 0;
    GROUPS.forEach((g) => {
      state[g].forEach((key) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'snk-chip';
        chip.setAttribute('aria-label', `${T('remove_filter', 'Remove filter:')} ${labelFor(g, key)}`);
        chip.append(g === 'free' ? T('no_x', 'No {x}').replace('{x}', labelFor(g, key)) : labelFor(g, key));
        chip.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M6 6l8 8M14 6l-8 8"/></svg>');
        chip.addEventListener('click', () => {
          state[g].delete(key);
          update();
        });
        els.active.append(chip);
      });
    });
    if (n > 1) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'snk-chip snk-chip--clear';
      clear.textContent = T('clear_all', 'Clear all');
      clear.addEventListener('click', clearFilters);
      els.active.append(clear);
    }
  }

  /* ───────── filter sheet ───────── */

  const GROUP_DEFS = [
    { key: 'brand', title: T('brand', 'Brand'), of: (p) => [p.brand], order: 'label' },
    { key: 'flavor', title: T('flavor', 'Flavor'), of: (p) => [p.flavor], order: 'count' },
    { key: 'texture', title: T('texture', 'Texture'), of: (p) => [p.texture], order: 'count' },
    {
      key: 'free',
      title: T('free_from', 'Free from'),
      note: T('free_note', 'Hides products that list this allergen. Always check the package label, especially if you have a food allergy.'),
      of: (p) => p.allergens,
      order: 'label',
    },
    { key: 'price', title: T('price', 'Price'), of: (p) => PRICE.filter((b) => b.test(p.price)).map((b) => b.key), order: 'fixed' },
  ];

  function optionsFor(def) {
    const base = [...products.values()].filter((p) => match(p, def.key));
    const counts = new Map();
    if (def.key === 'free') {
      // how many products stay visible if this allergen is also excluded
      const present = new Set(base.flatMap((p) => p.allergens));
      state.free.forEach((a) => present.add(a));
      present.forEach((a) => {
        counts.set(a, base.filter((p) => !p.allergens.some((x) => x === a || state.free.has(x))).length);
      });
    } else {
      base.forEach((p) => def.of(p).forEach((k) => k && counts.set(k, (counts.get(k) || 0) + 1)));
      state[def.key].forEach((k) => counts.has(k) || counts.set(k, 0));
    }
    let opts = [...counts.entries()].map(([key, count]) => ({ key, count, label: labelFor(def.key, key) }));
    if (def.order === 'label') opts.sort((a, b) => collator.compare(a.label, b.label));
    if (def.order === 'count') opts.sort((a, b) => b.count - a.count || collator.compare(a.label, b.label));
    if (def.order === 'fixed') opts.sort((a, b) => PRICE.findIndex((x) => x.key === a.key) - PRICE.findIndex((x) => x.key === b.key));
    return opts;
  }

  function renderGroups() {
    if (!els.groups) return;
    const scroll = els.groups.scrollTop;
    els.groups.replaceChildren();
    GROUP_DEFS.forEach((def) => {
      const opts = optionsFor(def);
      if (opts.length < 2 && !state[def.key].size) return;
      const sectionEl = document.createElement('section');
      sectionEl.className = 'snk-fg';
      const title = document.createElement('h3');
      title.className = 'snk-fg__title';
      title.textContent = def.title;
      sectionEl.append(title);
      if (def.note) {
        const note = document.createElement('p');
        note.className = 'snk-fg__note';
        note.textContent = def.note;
        sectionEl.append(note);
      }
      const wrap = document.createElement('div');
      wrap.className = 'snk-fg__opts';
      const limit = expanded.has(def.key) ? Infinity : 12;
      let shownCount = 0;
      opts.forEach((o) => {
        const selected = state[def.key].has(o.key);
        if (!selected && shownCount >= limit) return;
        shownCount++;
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'snk-chip';
        chip.setAttribute('aria-pressed', String(selected));
        chip.disabled = !selected && o.count === 0;
        chip.append(o.label, ' ');
        const n = document.createElement('span');
        n.className = 'snk-chip__n';
        n.textContent = String(o.count);
        chip.append(n);
        chip.addEventListener('click', () => {
          selected ? state[def.key].delete(o.key) : state[def.key].add(o.key);
          update();
        });
        wrap.append(chip);
      });
      sectionEl.append(wrap);
      if (opts.length > shownCount) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'snk-fg__more';
        more.textContent = T('show_all_n', 'Show all {n}').replace('{n}', opts.length);
        more.addEventListener('click', () => {
          expanded.add(def.key);
          renderGroups();
        });
        sectionEl.append(more);
      }
      els.groups.append(sectionEl);
    });
    els.groups.scrollTop = scroll;
  }

  function hasFilterOptions() {
    return GROUP_DEFS.some((def) => optionsFor(def).length >= 2);
  }

  /* ───────── actions ───────── */

  function update() {
    writeUrl();
    render(true);
  }

  function clearFilters() {
    GROUPS.forEach((g) => state[g].clear());
    update();
  }

  // Horizon scrolls .page-wrapper (not the window) on desktop, so work with positions on screen
  // and scrollIntoView, which scroll whichever container actually scrolls.
  const headerHeight = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--snk-header-h')) || 0;

  function scrollToResults() {
    const limit = headerHeight() + (els.sticky ? els.sticky.offsetHeight : 0) + 8;
    if (grid.getBoundingClientRect().top >= limit) return;
    grid.style.scrollMarginTop = `${limit}px`;
    grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  typeChips.forEach((chip) => {
    chip.addEventListener('click', () => {
      state.type = chip.getAttribute('data-type-chip') || '';
      update();
      chip.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      scrollToResults();
    });
  });

  treeTypeLinks.forEach((a) => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      const key = a.getAttribute('data-type-link') || '';
      state.type = state.type === key ? '' : key;
      update();
      scrollToResults();
    });
  });

  els.sort?.addEventListener('change', () => {
    state.sort = els.sort.value;
    update();
    if (state.sort === 'popular' && !rank) loadRank().then(() => render(true), () => {});
    scrollToResults();
  });

  $$('[data-clear-all]').forEach((b) => b.addEventListener('click', clearFilters));

  if (els.sheet) {
    const sheet = els.sheet;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    // slide the panel back out, then close (close button, "Show results", backdrop, Esc)
    const closeSheet = () => {
      if (!sheet.open || sheet.classList.contains('is-closing')) return;
      if (reduced.matches) {
        sheet.close();
        return;
      }
      sheet.classList.add('is-closing');
      const done = () => {
        if (!sheet.classList.contains('is-closing')) return;
        sheet.classList.remove('is-closing');
        sheet.close();
      };
      sheet.querySelector('.snk-sheet__panel')?.addEventListener('animationend', done, { once: true });
      window.setTimeout(done, 400);
    };
    els.filterBtn?.addEventListener('click', () => {
      renderGroups();
      sheet.showModal();
      // start on the sheet itself rather than the close button, so Safari doesn't ring the ✕
      /** @type {HTMLElement | null} */ (sheet.querySelector('.snk-sheet__panel'))?.focus({ preventScroll: true });
      document.documentElement.style.overflow = 'hidden';
    });
    sheet.querySelector('form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      closeSheet();
    });
    sheet.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeSheet();
    });
    sheet.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      if (replayOnClose) {
        replayOnClose = false;
        window.SnakeReveal?.replay?.(grid);
      }
      scrollToResults();
    });
    sheet.addEventListener('click', (e) => {
      if (e.target === sheet) closeSheet();
    });
  }

  if (els.sticky) {
    const sticky = els.sticky;
    let ticking = false;
    const check = () => {
      ticking = false;
      sticky.classList.toggle('is-stuck', root.getBoundingClientRect().top < 0 && sticky.getBoundingClientRect().top <= headerHeight() + 1);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(check);
      }
    };
    [window, document.querySelector('.page-wrapper')].forEach((el) => el?.addEventListener('scroll', onScroll, { passive: true }));
  }

  /* ───────── data loading ───────── */

  async function loadRank() {
    try {
      const cached = JSON.parse(sessionStorage.getItem('snk-rank') || 'null');
      if (cached && Date.now() - cached.t < 10 * 60 * 1000) {
        rank = new Map(cached.ids.map((id, i) => [String(id), i]));
        return rank;
      }
    } catch (e) {}
    const ids = [];
    let page = 1;
    let pages = 1;
    do {
      const res = await fetch(`${shopRoot}collections/all?sort_by=best-selling&section_id=snake-rank&page=${page}`);
      const text = await res.text();
      const json = text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1);
      const data = JSON.parse(json);
      ids.push(...data.ids);
      pages = data.pages || 1;
      page++;
    } while (page <= pages && page <= 40);
    rank = new Map(ids.map((id, i) => [String(id), i]));
    try {
      sessionStorage.setItem('snk-rank', JSON.stringify({ t: Date.now(), ids }));
    } catch (e) {}
    return rank;
  }

  async function loadRemainingPages() {
    if (!(cfg.pages > 1) || cfg.page !== 1) return;
    if (els.loading) els.loading.hidden = false;
    try {
      for (let page = 2; page <= cfg.pages; page++) {
        // same request as this page (keeps q / view / type for search), minus our own filter params
        const url = new URL(location.href);
        [...(isSearch ? [] : ['type']), 'sort', ...GROUPS].forEach((k) => url.searchParams.delete(k));
        url.hash = '';
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', cfg.sectionId);
        const html = await (await fetch(url.toString())).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        addCards($$('[data-snk-grid] [data-snc]', doc).map((n) => document.adoptNode(n)));
        render();
      }
    } catch (e) {
      console.warn('[snake-collection] could not load every page', e);
    } finally {
      if (els.loading) els.loading.hidden = true;
      if (els.filterBtn) els.filterBtn.hidden = !hasFilterOptions();
    }
  }

  /* ───────── moving between categories ─────────
     Tapping another category in the row loads its page. Show the choice straight away (the ring moves)
     and dim the current snacks until it arrives; undone if the shopper comes back with Back. */
  const l2Row = $('[data-l2-row]');
  if (l2Row) {
    const items = $$('.snk-l2__item', l2Row);
    const initialActive = items.find((a) => a.classList.contains('is-active'));
    let leaveTimer = 0;
    const reset = () => {
      clearTimeout(leaveTimer);
      root.classList.remove('is-leaving');
      items.forEach((a) => a.classList.toggle('is-active', a === initialActive));
    };
    l2Row.addEventListener('click', (e) => {
      const me = /** @type {MouseEvent} */ (e);
      const a = /** @type {HTMLElement} */ (me.target).closest('a.snk-l2__item');
      if (!a || me.defaultPrevented || me.button !== 0 || me.metaKey || me.ctrlKey || me.shiftKey || me.altKey) return;
      items.forEach((x) => x.classList.toggle('is-active', x === a));
      root.classList.add('is-leaving');
      // if the next page never comes (offline, cancelled), don't leave the snacks dimmed
      leaveTimer = window.setTimeout(reset, 8000);
    });
    window.addEventListener('pageshow', (e) => {
      if (/** @type {PageTransitionEvent} */ (e).persisted) reset();
    });
  }

  /* ───────── go ───────── */

  readUrl();
  render();
  if (els.filterBtn) els.filterBtn.hidden = !hasFilterOptions();
  if (state.sort === 'popular') loadRank().then(() => render(true), () => {});
  if (state.sort !== DEFAULT_SORT || state.type || activeCount()) writeUrl();
  loadRemainingPages();
})();
